// React and core imports
import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";

// Material-UI core components
import Grid from "@mui/material/Grid";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";

// Material-UI data display components
import {
  Chip,
  Divider,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Card,
  CardContent,
} from "@mui/material";

// Material-UI dialog components
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

// Material-UI feedback components
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

// Material-UI accordion components
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

// Material-UI icons
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import TextSnippetOutlinedIcon from "@mui/icons-material/TextSnippetOutlined";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  ArrowOutwardRounded,
  ChatBubbleOutlineOutlined,
  ChatOutlined,
  TextSnippetOutlined,
  Send,
  Refresh,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import { Tooltip, IconButton } from "@mui/material";
import { CheckCircle, Warning, Error, Close } from "@mui/icons-material";

// Material-UI date picker components
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Calendar } from "@hassanmojab/react-modern-calendar-datepicker";

// Third-party libraries
import { useSnackbar } from "notistack";
import moment from "moment";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import jwtDecode from "jwt-decode";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

// Local imports - authentication
import { useAuth } from "../../../../auth";
// Local imports - API functions
import {
  getSessionNotes,
  getSessionNotesById,
  getToken,
} from "../../APIS/clientAPIS";
// Local imports - tab components
import CourseTabItem from "../../tabs/clients/CourseTabItem";
import ScreenerTabItem from "../../tabs/clients/ScreenerTabItem";
import AppointmentTabItem from "../../tabs/clients/AppointmentTabItem";
import NoteTabItem from "../../tabs/clients/NoteTabItem";
import PrescriptionTabItem from "../../tabs/clients/PrescriptionTabItem";
import SessionNoteTabItem from "../../tabs/clients/SessionNoteTabItem";
// Local imports - modals
import AddAppointmentModal from "../../modals/AddAppointmentModal";
// Local imports - helpers
import { NoDataSvg } from "../../helpers/Svg";
// Local imports - client components
import Documents from "./ClientComponents/Documents";
import WorkSheets from "./ClientComponents/WorkSheets";
import Assessments from "./ClientComponents/Assessments";
import axios from "axios";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Helper function to safely format dates
const formatDateSafely = (dateString) => {
  if (!dateString) {
    return "N/A";
  }

  // Check if the dateString is already formatted (contains "AM" or "PM")
  if (
    typeof dateString === "string" &&
    (dateString.includes("AM") || dateString.includes("PM"))
  ) {
    // If it's already formatted like "29 Jul 2025 09:29 AM", return as is
    return dateString;
  }

  try {
    // Check if the dateString is in UTC format (ISO string or contains 'Z' or 'T')
    const isUTC =
      typeof dateString === "string" &&
      (dateString.includes("T") ||
        dateString.includes("Z") ||
        dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/));

    if (isUTC) {
      // Convert UTC to local time using dayjs
      return dayjs.utc(dateString).local().format("DD MMM YYYY hh:mm A");
    }

    // For non-UTC dates, use the original logic
    const date = new Date(dateString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }

    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch (error) {
    console.error("Error formatting date:", error, dateString);
    return "Invalid Date";
  }
};

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <div>{children}</div>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ClientDetailsView = ({
  clientName,
  onScreenerView,
  clientID,
  clientDetails,
}) => {
  // console.log("clientName", clientName)
  // console.log("clientID", clientID)
  // console.log("clientDetails", clientDetails);
  const [selectedTab, setSelectedTab] = useState(0);
  const [clientViewDetails, setClientViewDetails] = useState({});
  const [checkClientID, setCheckClientID] = useState(clientID);
  const [clientAppointmentsList, setClientAppointmentsList] = useState([]);
  const [clientSessionNotes, setClientSessionNotes] = useState([]);
  const [clientPrescriptions, setClientPrescriptions] = useState([]);
  const [clientScreeners, setClientScreeners] = useState([]);
  const [mergedClientScreeners, setMergedClientScreeners] = useState([]);
  const [clientScreenersQNA, setClientScreenersQNA] = useState([]);
  const [clientCourses, setClientCourses] = useState([]);
  const [openSessionNotesModal, setOpenSessionNotesModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState("");
  const [chatClientList, setChatClientList] = useState([]);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [bearerToken, setBearerToken] = useState(localStorage.getItem("token"));
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [profile, setProfile] = useState({});
  const [sessionNoteView, setSessionNoteView] = useState({});

  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  // Edit modal states
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    phone: "",
    dob: null,
    gender: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Notes/Comment states
  const [comment, setComment] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [existingComments, setExistingComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentsPage, setCommentsPage] = useState(1);
  const [totalCommentsPages, setTotalCommentsPages] = useState(1);

  const headers = {
    "X-API-Key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
    "Content-Type": "application/json",
  };

  const { currentUser, userType, logout } = useAuth();
  const navigate = useNavigate();
  const searchBarParams = useSearchParams();

  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(0);
  const handleChangePage = (event, value) => {
    setPage(value);
  };

  const { search } = useLocation();
  const tabIndexFromURL = new URLSearchParams(search).get("tabIndex");

  //console.log("queryParam", checkClientID)

  //if tabIndexFromURL is present
  useEffect(() => {
    setSelectedTab(+tabIndexFromURL);
  }, []);

  useEffect(() => {
    setCheckClientID(clientID);
    setClientViewDetails(clientDetails);
  }, [clientID]);

  useEffect(() => {
    if (checkClientID) {
      const fetchClientDetails = async () => {
        try {
          const response = await fetch(
            `https://cauth.unitedwecare.com/users/get-dashboard-profile/${checkClientID}`,
            {
              method: "GET",
              headers,
              "x-api-key": process.env.REACT_APP_ADMIN_API_KEY,
            }
          );
          
          if (!response.ok) {
            let errorMessage = "Failed to load client details. Please try again.";
            switch (response.status) {
              case 404:
                errorMessage = "Client not found. Please check the client ID.";
                break;
              case 401:
                errorMessage = "You are not authorized to view this client's details.";
                break;
              case 403:
                errorMessage = "You don't have permission to access this client's information.";
                break;
              case 500:
              case 502:
              case 503:
              case 504:
                errorMessage = "Server is temporarily unavailable. Please try again in a few minutes.";
                break;
            }
            enqueueSnackbar(errorMessage, { variant: "error" });
            return;
          }
          
          const postResponse = await response.json();
          const client_details = await postResponse.data;
          setProfile(client_details);
        } catch (error) {
          console.error("Error:", error);
          enqueueSnackbar("Unable to load client details. Please check your connection and try again.", { variant: "error" });
        }
      };
      fetchClientDetails();
    }
  }, [checkClientID]);

  //Snackbar
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenSnackbar(false);
  };

  //Check if CLient ID
  const getClientID = () => {
    if (
      (checkClientID === "" ||
        checkClientID === null ||
        checkClientID === undefined) &&
      window.location.href.split("?")[1].includes("id")
    ) {
      const queryParam = new URLSearchParams(search).get("id");
      setCheckClientID(queryParam);
    } else {
      setCheckClientID(clientID);
    }
  };

  useEffect(() => {
    getClientID();
  }, []);

  // Define the sorting function
  const sortByDate = (a, b) => {
    // Extract score_date or create_date from each item
    let dateA = a.score_date || a.create_date;
    let dateB = b.score_date || b.create_date;

    // Convert to date objects
    dateA = new Date(dateA);
    dateB = new Date(dateB);

    // Compare the dates
    return dateB - dateA; // This will sort in ascending order
  };

  //Fetch Client Screeners
  useEffect(() => {
    if (!checkClientID) {
      // If checkClientID is blank, return early
      return;
    }

    ///ADDED API IN CLIENTAPIS
    ///IMPLEMENTATION PENDING

    const postClientScreenersData = async () => {
      try {
        const screenerBody = {
          expert_id: currentUser?.UserId,
          user_id: checkClientID,
          // user_id: currentUser?.UserId,
        };

        const response = await fetch(
          `https://odoo.unitedwecare.ca/new-uwc/expert/dashboard/assignments`,
          {
            method: "POST",
            headers,
            body: JSON.stringify(screenerBody),
          }
        );
        
        if (!response.ok) {
          let errorMessage = "Failed to load client assessments. Please try again.";
          switch (response.status) {
            case 404:
              errorMessage = "No assessments found for this client.";
              break;
            case 401:
              errorMessage = "You are not authorized to view client assessments.";
              break;
            case 403:
              errorMessage = "You don't have permission to access client assessments.";
              break;
            case 500:
            case 502:
            case 503:
            case 504:
              errorMessage = "Assessment service is temporarily unavailable. Please try again later.";
              break;
          }
          enqueueSnackbar(errorMessage, { variant: "error" });
          return;
        }
        
        const postResponse = await response.json();

        const { assignments } = await postResponse.data;
        const { worksheets } = await postResponse.data;

        setClientScreeners(assignments);
        setClientScreenersQNA(worksheets);

        // Modify worksheets to include score_date and type
        const modifiedWorksheets = worksheets.map((worksheet) => ({
          ...worksheet,
          score_date: worksheet.create_date,
          type: "QNA", // Copy create_date to score_date
        }));

        // Modify assignments to include score_date
        const modifiedAssignments = assignments.map((worksheet) => ({
          ...worksheet,
          type: "Options",
        }));

        // Merge the arrays
        const mergedClientScreeners = [
          ...modifiedAssignments,
          ...modifiedWorksheets,
        ];
        // Sort the merged array
        const sortedMergedClientScreeners =
          mergedClientScreeners.sort(sortByDate);

        setMergedClientScreeners(sortedMergedClientScreeners);
      } catch (error) {
        console.error("Error:", error);
        enqueueSnackbar("Unable to load client assessments. Please check your connection and try again.", { variant: "error" });
      }
    };

    postClientScreenersData();
  }, [checkClientID]);

  //Fetch Client Appointments
  useEffect(() => {
    if (!checkClientID) {
      // If checkClientID is blank, return early
      return;
    }

    const payload = {
      expert_id: currentUser?.UserId,
      user_id: Number(checkClientID),
      page: page,
    };

    const postClientAppointmentsData = async () => {
      try {
        const response = await fetch(
          `https://odoo.unitedwecare.ca/new-uwc/expert/dashboard/appointments`,
          {
            method: "POST",
            headers,
            body: JSON.stringify(payload),
          }
        );
        
        if (!response.ok) {
          let errorMessage = "Failed to load client appointments. Please try again.";
          switch (response.status) {
            case 404:
              errorMessage = "No appointments found for this client.";
              break;
            case 401:
              errorMessage = "You are not authorized to view client appointments.";
              break;
            case 403:
              errorMessage = "You don't have permission to access client appointments.";
              break;
            case 500:
            case 502:
            case 503:
            case 504:
              errorMessage = "Appointment service is temporarily unavailable. Please try again later.";
              break;
          }
          enqueueSnackbar(errorMessage, { variant: "error" });
          return;
        }
        
        const postResponse = await response.json();

        const { appointments, total_pages } = await postResponse.data;

        setClientAppointmentsList(appointments);
        setTotalPage(total_pages);
        //setUserData(postResponse?.data);

        //console.log("data", data);
      } catch (error) {
        console.error("Error:", error);
        enqueueSnackbar("Unable to load client appointments. Please check your connection and try again.", { variant: "error" });
      }
    };

    postClientAppointmentsData();
    setLoading(false);
  }, [checkClientID, page]);

  //Fetch Client Prescriptions
  useEffect(() => {
    if (!checkClientID) {
      // If checkClientID is blank, return early
      return;
    }

    //console.log("userAppointmentItem", userAppointmentItem)
    let sessionBearerToken = sessionStorage.getItem("token");

    const prescriptionHeaders = {
      Authorization: `Bearer ${sessionBearerToken}`,
      "Content-Type": "application/json",
      deviceid: "123",
      devicetype: "web",
      lat: "1",
      long: "1",
    };

    const postClientNotesData = async (retryCount = 0) => {
      const MAX_RETRY = 3; // Maximum number of retries
      try {
        const response = await fetch(
          `https://app.uwc.world/api/appointment/GetAppointment_Documents_Files_Sent_Expert?AppointmentId=0&ToUserId=${checkClientID}`,
          {
            method: "GET",
            headers: prescriptionHeaders,
          }
        );
        
        if (!response.ok) {
          let errorMessage = "Failed to load client prescriptions. Please try again.";
          switch (response.status) {
            case 404:
              errorMessage = "No prescriptions found for this client.";
              break;
            case 401:
              errorMessage = "You are not authorized to view client prescriptions.";
              break;
            case 403:
              errorMessage = "You don't have permission to access client prescriptions.";
              break;
            case 500:
            case 502:
            case 503:
            case 504:
              errorMessage = "Prescription service is temporarily unavailable. Please try again later.";
              break;
          }
          enqueueSnackbar(errorMessage, { variant: "error" });
          return;
        }
        
        const postResponse = await response.json();

        //console.log("postResponse PRES", postResponse)

        if (postResponse?.ResponseCode == 401 && retryCount < MAX_RETRY) {
          getToken(currentUser?.Emailid).then((res) => {
            setTimeout(() => postClientNotesData(retryCount + 1), 0);
          });
        }

        if (postResponse?.ResponseCode == 200) {
          const token = postResponse?.data;
          const decodedToken = jwtDecode(token);

          //const { prescriptions } = await postResponse.data;
          //console.log("userAppointmentID", userAppointmentID)
          //console.log("decodedToken", decodedToken);
          setClientPrescriptions(decodedToken);
        }

        //setUserData(postResponse?.data);

        //console.log("data", data);
      } catch (error) {
        console.error("Error:", error);
        enqueueSnackbar("Unable to load client prescriptions. Please check your connection and try again.", { variant: "error" });
      }
    };

    postClientNotesData(0);
    setLoading(false);
  }, [chatClientList]);

  //Fetch Client Courses
  useEffect(() => {
    if (!checkClientID) {
      // If checkClientID is blank, return early
      return;
    }
    const payload = {
      userId: checkClientID,
    };

    const postClientNotesData = async () => {
      try {
        const response = await fetch(
          `https://odoo.unitedwecare.ca/new-uwc/community/course/active_courses`,
          {
            method: "POST",
            headers,
            body: JSON.stringify(payload),
          }
        );
        
        if (!response.ok) {
          let errorMessage = "Failed to load client courses. Please try again.";
          switch (response.status) {
            case 404:
              errorMessage = "No courses found for this client.";
              break;
            case 401:
              errorMessage = "You are not authorized to view client courses.";
              break;
            case 403:
              errorMessage = "You don't have permission to access client courses.";
              break;
            case 500:
            case 502:
            case 503:
            case 504:
              errorMessage = "Course service is temporarily unavailable. Please try again later.";
              break;
          }
          enqueueSnackbar(errorMessage, { variant: "error" });
          return;
        }
        
        const postResponse = await response.json();

        const { courses } = await postResponse.data;

        setClientCourses(courses);
        //setUserData(postResponse?.data);

        //console.log("data", data);
      } catch (error) {

        enqueueSnackbar("Unable to load client courses. Please check your connection and try again.", { variant: "error" });
      }
    };

    postClientNotesData();
    setLoading(false);
  }, [checkClientID]);

  //Fetch Client SessionNotes
  useEffect(() => {
    const fetchSessionNotes = async () => {
      try {
        const sessionData = await getSessionNotes(
          currentUser.UserId,
          checkClientID
        );

        setClientSessionNotes(sessionData?.notes);
        setLoading(false);
        // console.log("sessionData", sessionData);

        //setAppointmentsData(appointments);
      } catch (error) {
        console.error("Error fetching sessionNotes:", error);
      }
    };

    if (!checkClientID) {
      // If checkClientID is blank, return early
      return;
    }

    if (checkClientID !== undefined) {
      fetchSessionNotes();
    }
  }, [checkClientID]);

  const handleChangeTab = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Edit modal functions
  const handleEditOpen = () => {
    // Split the full name into firstName, middleName, and lastName
    const firstName = profile?.first_name || "";
    const middleName = profile?.middle_name || "";
    const lastName = profile?.last_name || "";
    
    setEditFormData({
      firstName: firstName,
      middleName: middleName,
      lastName: lastName,
      email: profile?.email || "",
      phone: profile?.phone || "",
      dob: profile?.dob || null,
      gender: profile?.gender || "",
    });
    setOpenEditModal(true);
  };
  
  const handleEditClose = () => {
    setOpenEditModal(false);
    setEditFormData({
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      phone: "",
      dob: null,
    });
  };

  const handleFormChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      // Get the bearer token from localStorage Authorization
      const authHeader = localStorage.getItem("Authorization");
      const token = authHeader ? authHeader.split(" ")[1] : null;
      
      if (!token) {
        throw new Error("Authentication token not found");
      }

      // Prepare the API payload with phone number and email
      const payload = {
        user_id: checkClientID,
        firstName: editFormData.firstName,
        middleName: editFormData.middleName,
        lastName: editFormData.lastName,
        phone: editFormData.phone,
        email: editFormData.email,
        dob: editFormData.dob,
        gender: editFormData.gender,
      };

      // Prepare headers similar to other successful API calls
      const updateHeaders = {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
        "x-api-key": process.env.REACT_APP_ADMIN_API_KEY,
      };

      // Make API call to update client details
      const response = await fetch(
        "https://cauth.unitedwecare.com/users/v2/update-user-details",
        {
          method: "POST",
          headers: updateHeaders,
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        // Handle different error scenarios with user-friendly messages
        let userErrorMessage = "Failed to update client details. Please try again.";
        
        switch (response.status) {
          case 400:
            userErrorMessage = "Invalid information provided. Please check your input and try again.";
            break;
          case 404:
            userErrorMessage = "Client not found. Please refresh the page and try again.";
            break;
          case 409:
            userErrorMessage = "This email or phone number is already in use by another client.";
            break;
          case 422:
            userErrorMessage = "Please check that all required fields are filled correctly.";
            break;
          case 500:
          case 502:
          case 503:
          case 504:
            userErrorMessage = "Server is temporarily unavailable. Please try again in a few minutes.";
            break;
          default:
            userErrorMessage = "An unexpected error occurred. Please try again later.";
        }
        
        throw new Error(userErrorMessage);
      }

      const result = await response.json();
      
      // Update local state with the new data
      const nameParts = [
        editFormData.firstName,
        editFormData.middleName,
        editFormData.lastName
      ].filter(part => part && part.trim().length > 0);
      const fullName = nameParts.join(" ").trim();
      setProfile((prev) => ({
        ...prev,
        name: fullName,
        email: editFormData.email,
        phone: editFormData.phone,
        dob: editFormData.dob,
        gender: editFormData.gender,
      }));

      enqueueSnackbar("Client details updated successfully!", {
        variant: "success",
      });
      handleEditClose();
    } catch (error) {
      console.error("Error updating client details:", error);
      enqueueSnackbar(
        error.message || "Failed to update client details", 
        {
          variant: "error",
        }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewScreener = (title, id, type) => {
    //console.log("mid", id)
    onScreenerView(title, id, type);
  };

  ///Session Notes Region Start

  const handleSessionNotesClose = () => {
    setOpenSessionNotesModal(false);
  };

  const handleDeleteOpen = () => {
    setOpenDeleteModal(true);
    setDeleteConfirmationText("");
  };

  const handleDeleteClose = () => {
    setOpenDeleteModal(false);
    setDeleteConfirmationText("");
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmationText === "DELETE") {
        console.log("Deleting client:", profile?.user_id);
        setIsDeleting(true);
        try {
    
          const response = await fetch(
            `https://cauth.unitedwecare.com/users/${profile?.user_id}?region=${profile?.country?.toLowerCase() == "australia" ? "australia" : "global"}&force=false&email=${profile?.email}`,
            {
              method: "DELETE",
              headers: {
                "x-api-key": process.env.REACT_APP_EXPERT_API_KEY,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(currentUser),
            }
          );
          if(response?.status == 200){
            enqueueSnackbar("Client deleted successfully", { variant: "success" });
            // Navigate back to patient listing after successful deletion
            setTimeout(() => {
              navigate('/expert/patients');
            }, 1500); // Small delay to show the success message
          } else {
            enqueueSnackbar("Failed to delete client. Please try again.", { variant: "error" });
          }
        } catch (error) {
          console.error("Error deleting client:", error);
          enqueueSnackbar("Failed to delete client. Please try again.", { variant: "error" });
        } finally {
          setIsDeleting(false);
          handleDeleteClose();        
        }
    } else {
      enqueueSnackbar("Please type 'DELETE' to confirm", { variant: "warning" });
    }
  };

  const fetchSessionNotesById = async (id) => {
    try {
      const sessionDataID = await getSessionNotesById(currentUser?.UserId, id);

      setSessionNoteView(sessionDataID?.notes);

      if (sessionDataID?.notes !== undefined) {
        setOpenSessionNotesModal(true);
      } else {
        enqueueSnackbar("Oops! Looks like this Record doesn't exist!", {
          variant: "error",
        });
        // alert("Oops! Looks like this Record doesn't exist!");
      }

      //console.log("sessionDataID?.notes", sessionDataID)
    } catch (error) {
      console.error("Error fetching session notes by ID:", error);
    }
  };

  const sortedClientSessionNotes = clientSessionNotes?.sort(function (a, b) {
    var dateA = new Date(a.date);
    var dateB = new Date(b.date);

    return dateB - dateA;
  });

  ///Session Notes Region End

  // Notes/Comment Functions
  const fetchComments = async (page = 1) => {
    try {
      setLoadingComments(true);

      // Ensure we have valid IDs for the API call
      const patientId = parseInt(checkClientID) || "";
      const expertId = parseInt(currentUser?.UserId) || "";

      // Validate that we have all required parameters
      if (!patientId || !expertId) {
        setExistingComments([]);
        return;
      }

      // Build query parameters with pagination
      const params = new URLSearchParams({
        patient_id: patientId,
        expert_id: expertId,
        page: page.toString(),
        count: "10",
      });

      const response = await fetch(
        `https://us.uwc.world/new-uwc/expert/dashboard/get-expert-comments?${params}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
          },
        }
      );

      if (response.ok) {
        const result = await response.json();

        if (
          result &&
          result.code === 200 &&
          result.data &&
          result.data.comments &&
          Array.isArray(result.data.comments)
        ) {
          const transformedComments = result.data.comments.map((comment) => ({
            id: comment.id || Date.now(),
            text: comment.comment || comment.text || "",
            author:
              comment.expert?.name ||
              comment.expert_name ||
              comment.author ||
              currentUser?.Name ||
              "Expert",
            timestamp: (() => {
              const rawDate = comment.created_at || comment.timestamp;
              if (typeof rawDate === "string") {
                let cleanDate = rawDate;
                const match = rawDate.match(/(\d{2}):(\d{2}) (AM|PM)/);
                if (match) {
                  const hour = parseInt(match[1]);
                  const minutes = match[2];
                  if (hour > 12) {
                    const hour12 = hour === 12 ? 12 : hour % 12;
                    cleanDate = rawDate.replace(
                      `${hour}:${minutes}`,
                      `${hour12}:${minutes}`
                    );
                  }
                }
                const utcDate = dayjs.utc(cleanDate, "DD MMM YYYY hh:mm A");
                if (utcDate.isValid()) {
                  return utcDate
                    .tz("Asia/Calcutta")
                    .format("DD MMM YYYY hh:mm A");
                }
              }
              return rawDate || "Invalid Date";
            })(),
            type: comment.type || "comment",
            expertImage: comment.expert?.image || "",
            patientImage: comment.patient?.image || "",
          }));

          setExistingComments(transformedComments);
          setTotalCommentsPages(result.data.total_page_count || 1);
          setCommentsPage(page);
        } else {
          setExistingComments([]);
          setTotalCommentsPages(1);
        }
      } else {
        setExistingComments([]);
        setTotalCommentsPages(1);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
      setExistingComments([]);
      setTotalCommentsPages(1);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleSendComment = async () => {
    if (comment.trim()) {
      setIsSubmittingComment(true);
      try {
        // Ensure we have valid IDs for the API call
        const patientId = parseInt(checkClientID) || 0;
        const expertId = parseInt(currentUser?.UserId) || 0;

        // Validate that we have all required parameters
        if (!patientId || !expertId) {
          console.warn("Missing required parameters for adding comment:", {
            patientId,
            expertId,
          });
          enqueueSnackbar(
            "Missing required information. Please refresh the page.",
            { variant: "error" }
          );
          return;
        }

        const payload = {
          patient_id: patientId,
          expert_id: expertId,
          comment: comment.trim(),
        };

        const response = await fetch(
          "https://us.uwc.world/new-uwc/expert/dashboard/add-expert-comment",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-api-key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
            },
            body: JSON.stringify(payload),
          }
        );

        if (response.ok) {
          const result = await response.json();

          // Add a small delay to ensure server has processed the comment
          setTimeout(async () => {
            await fetchComments(1);
          }, 1000);

          setComment("");
          enqueueSnackbar("Comment added successfully", { variant: "success" });
        } else {
          let errorMessage = "Failed to add comment. Please try again.";
          switch (response.status) {
            case 400:
              errorMessage = "Invalid comment. Please check your input and try again.";
              break;
            case 401:
              errorMessage = "You are not authorized to add comments.";
              break;
            case 403:
              errorMessage = "You don't have permission to comment on this client.";
              break;
            case 500:
            case 502:
            case 503:
            case 504:
              errorMessage = "Comment service is temporarily unavailable. Please try again later.";
              break;
          }
          enqueueSnackbar(errorMessage, { variant: "error" });
        }
      } catch (error) {
        console.error("Error adding comment:", error);
        enqueueSnackbar("Failed to add comment. Please try again.", {
          variant: "error",
        });
      } finally {
        setIsSubmittingComment(false);
      }
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendComment();
    }
  };

  // Pagination handlers
  const handlePreviousPage = () => {
    if (commentsPage > 1) {
      fetchComments(commentsPage - 1);
    }
  };

  const handleNextPage = () => {
    if (commentsPage < totalCommentsPages) {
      fetchComments(commentsPage + 1);
    }
  };

  // Fetch comments when client ID is available
  useEffect(() => {
    if (checkClientID && currentUser?.UserId) {
      fetchComments();
    }
  }, [checkClientID, currentUser?.UserId]);

  // Fetch comments when Notes tab is selected
  useEffect(() => {
    if (selectedTab === 4 && checkClientID && currentUser?.UserId) {
      fetchComments();
    }
  }, [selectedTab, checkClientID, currentUser?.UserId]);

  const normalizeDate = (date) => {
    // Handle null, undefined, or empty values
    if (!date) {
      return null;
    }

    // Try to parse the date with dayjs using different strategies
    let parsedDate = dayjs(date);
    
    // If first attempt fails, try parsing as DD-MM-YYYY format explicitly
    if (!parsedDate.isValid() && typeof date === 'string') {
      // Check if it matches DD-MM-YYYY pattern (e.g., "14-01-1995")
      const ddMmYyyyPattern = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
      const match = date.match(ddMmYyyyPattern);
      
      if (match) {
        const [, day, month, year] = match;
        // Create date with explicit format: YYYY-MM-DD
        parsedDate = dayjs(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
      }
    }
    
    // Check if the parsed date is valid
    if (!parsedDate.isValid()) {
      return null;
    }

    // Return formatted date in DD-MM-YYYY format
    return parsedDate.format("DD-MM-YYYY");
  }

  const [mrDisabled, setMrDisabled] = React.useState(false)
  const [medirecordsStatus, setMedirecordsStatus] = useState(false)
  const [toastShown, setToastShown] = useState(false)

  const searchMedirecords = async () => {
    try {
      let medirecords_response = await fetch(`https://cauth.unitedwecare.com/users/search-medirecords`, {
        method: "POST", 
        body: JSON.stringify({user_id: profile?.user_id}), 
        headers: {
          "X-API-Key": `${process.env.REACT_APP_EXPERT_API_KEY}`,
          "Content-Type": "application/json"
        }
      })
      if(!medirecords_response.ok){
        throw new Error("Network response was not ok");
      }
      let medirecords_data = await medirecords_response.json();
      console.log('medirecords_data', medirecords_data);
      setMedirecordsStatus(medirecords_data)
    } catch(error) {
      console.error(error);
    }
  }

  // Search medirecords when profile changes and country is Australia
  useEffect(() => {
    if (profile?.user_id && profile?.country?.toLowerCase() === "australia") {
      searchMedirecords();
      // Reset toast shown flag when profile changes
      setToastShown(false);
    }
  }, [profile?.user_id]);

  // Show sticky toast notification if is_toast is true
  useEffect(() => {
    if (medirecordsStatus?.data?.is_toast && !toastShown) {
      const link = medirecordsStatus?.data?.link;
      const message = medirecordsStatus?.data?.message || "Click to view";

      enqueueSnackbar(message, {
        variant: 'info',
        persist: true, // Makes it sticky
        action: (key) => (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {link && (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  window.open(link, '_blank', 'noopener,noreferrer');
                }}
                sx={{ color: 'white', textDecoration: 'underline' }}
              >
                View Link
              </Button>
            )}
            <IconButton
              size="small"
              onClick={() => {
                closeSnackbar(key);
              }}
              sx={{ color: 'white', padding: '4px' }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        )
      });
      
      setToastShown(true);
    }
  }, [medirecordsStatus, toastShown, enqueueSnackbar, closeSnackbar]);

  const handleCheckInMR = async () => {
    try {
      // setLoading(true);
      setMrDisabled(true)
      
      const response = await axios.post(
        'https://cauth.unitedwecare.com/users/sync-medirecords',
        {
          user_id: profile?.user_id,
          email: profile?.email
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': '76d802e426a2cb28f3760c8c8f669983f67ed775'
          }
        }
      );

      console.log('MediRecords sync response:', response.data);
      if(response?.status == 200){
        enqueueSnackbar(response?.data?.message || "PATIENT SYNCED SUCCESSFULLY", { variant: 'success' });
        // Refetch medirecords status after sync
        await searchMedirecords();
      }      
    } catch (error) {
      console.error('Error syncing MediRecords:', error);
      enqueueSnackbar(error?.response?.data?.message || "Failed to get status", { variant: 'error' });
    } finally {
      setMrDisabled(false)
      // setLoading(false);
    }
  }

  const tempAdminEmails=[
    "contactus@asksam.com.au",
    "ccop_aus1@tmail.com",
    "aussuperadmin@yopmail.com",
    "adminmel@asksam.com.au",
    "adminava@asksam.com.au",
    "adminnikki@asksam.com.au",
    "adminlucie@asksam.com.au",
    "adminsadie@asksam.com.au",
    "aloktest2@tmail.com",
    "sumitkhanna4444@gmail.com"
  ]

  return (
    <>
      <Grid container spacing={2} sx={{ marginTop: "20px" }}>
        <Grid item xs={8}>
          <div
            className="border p-3"
            style={{
              background: "#fff",
              borderRadius: "10px",
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={handleChangeTab}
              indicatorColor="primary"
              textColor="primary"
              className="ClientDetails--Tabs"
            >
              <Tab label="Appointments" />
              <Tab label="Documents" />
              {/* <Tab label="Worksheets" />
              <Tab label="Assessments" /> */}
              {/* <Tab label="Notes" /> */}
              {/* <Tab label="Prescriptions" /> */}
              <Tab label="Screeners" />
              <Tab label="Courses" />
              {/* <Tab label="Session Notes" /> */}
              <Tab label="Notes" {...a11yProps(4)} />
            </Tabs>

            {/* Appointments Panel */}
            <CustomTabPanel value={selectedTab} index={0}>
              <div className="Tab--Panel Appointments--Panel">
                {isLoading ? (
                  Array.from(new Array(10)).map((_, index) => (
                    <div
                      style={{
                        width: "100%",
                        height: "70px",
                        backgroundColor: "rgb(0,0,0,0.07)",
                        padding: "10px",
                        borderRadius: "10px",
                      }}
                      className="d-flex flex-column gap-2"
                    >
                      <Skeleton
                        variant="rounded"
                        width={75}
                        height={25}
                        sx={{ borderRadius: "20px" }}
                      />
                      <div className="d-flex flex-row gap-3">
                        <Skeleton
                          variant="rounded"
                          width="5vw"
                          height={20}
                          sx={{ borderRadius: "5px" }}
                        />
                        <Skeleton
                          variant="rounded"
                          width="5vw"
                          height={20}
                          sx={{ borderRadius: "5px" }}
                        />
                        <Skeleton
                          variant="rounded"
                          width="5vw"
                          height={20}
                          sx={{ borderRadius: "5px" }}
                        />
                      </div>
                    </div>
                  ))
                ) : clientAppointmentsList?.length > 0 ? (
                  <>
                    <Table stickyHeader>
                      {/* Table Head */}
                      <TableHead>
                        <TableRow>
                          <TableCell className="text-truncate">Status</TableCell>
                          <TableCell className="text-truncate">Date</TableCell>
                          <TableCell className="text-truncate">Time</TableCell>
                          <TableCell className="text-truncate">Duration</TableCell>
                          <TableCell className="text-truncate">Clinician</TableCell>
                          <TableCell className="text-truncate">Category</TableCell>
                          <TableCell className="text-truncate">Appointment Type</TableCell>

                        </TableRow>
                      </TableHead>

                      {/* Table Body */}
                      <TableBody>
                        {clientAppointmentsList?.map((item) => (
                          <AppointmentTabItem
                            ID={item.ID}
                            Status={item.Status}
                            AppDate={item.AppDate}
                            AppTime={item.AppTime}
                            duration={item.duration}
                            expertName={item.ExpertName}
                            appointmentType={item.client_type}
                            category={item.BookedRegisterAs}
                          />
                        ))}
                      </TableBody>
                    </Table>
                    <div className="d-flex justify-content-end w-100">
                      <Pagination
                        count={totalPage}
                        page={page}
                        onChange={handleChangePage}
                      />
                    </div>
                  </>
                ) : (
                  <div className="NoDataAvailable">
                    {NoDataSvg}
                    <p>No Appointments Booked</p>
                  </div>
                )}
              </div>
            </CustomTabPanel>

            {/* Documents Panel */}
            <CustomTabPanel value={selectedTab} index={1}>
              <Documents clientDetails={clientID} />
            </CustomTabPanel>

            {/* Worksheets Panel */}
            {/* <CustomTabPanel value={selectedTab} index={2}>
              <WorkSheets clientDetails={clientID} />
            </CustomTabPanel> */}

            {/* Assessments Panel */}
            {/* <CustomTabPanel value={selectedTab} index={3}>
              <Assessments clientDetails={clientID} />
            </CustomTabPanel> */}

            {/* Screeners Panel */}
            <CustomTabPanel value={selectedTab} index={2}>
              <div className="Tab--Panel Screeners--Panel">
                {loading ? (
                  Array.from(new Array(10)).map((_, index) => (
                    <div
                      style={{
                        width: "100%",
                        height: "70px",
                        backgroundColor: "rgb(0,0,0,0.07)",
                        padding: "10px",
                        borderRadius: "10px",
                      }}
                      className="d-flex justify-content-between"
                    >
                      <div className="d-flex flex-column gap-2">
                        <Skeleton
                          variant="rounded"
                          height={18}
                          width="20vw"
                          sx={{ borderRadius: "10px" }}
                        />
                        <Skeleton
                          variant="rounded"
                          height={18}
                          width="15vw"
                          sx={{ borderRadius: "10px" }}
                        />
                      </div>

                      <div className="CTA--Container d-flex gap-2 align-items-center">
                        <Skeleton
                          variant="rounded"
                          height={38}
                          width={86}
                          sx={{ borderRadius: "20px" }}
                        />
                      </div>
                    </div>
                  ))
                ) : mergedClientScreeners.length > 0 ? (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Submitted On</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {mergedClientScreeners.map((item) => (
                        <ScreenerTabItem
                          key={item.id}
                          type={item.type}
                          id={item.id}
                          title={item.name}
                          submittedOn={dayjs(item.score_date).format(
                            "DD MMM YYYY"
                          )}
                          onViewClick={handleViewScreener}
                        />
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="NoDataAvailable">
                    {NoDataSvg}
                    <p>No Screeners Taken</p>
                  </div>
                )}
              </div>
            </CustomTabPanel>

            {/* Courses Panel */}
            <CustomTabPanel value={selectedTab} index={3}>
              {loading ? (
                Array.from(new Array(10)).map((_, index) => (
                  <>
                    <div
                      className="d-flex flex-row gap-2 align-items-center"
                      style={{
                        // backgroundColor: "rgb(0,0,0,0.09)",
                        borderRadius: "10px",
                        padding: "5px",
                      }}
                    >
                      <Skeleton
                        width={122}
                        height={69}
                        sx={{ borderRadius: "10px" }}
                      />

                      <div className="d-flex flex-column gap-2">
                        <Skeleton
                          variant="rounded"
                          width="15vw"
                          height={20}
                          sx={{ borderRadius: "5px" }}
                        />
                        <Skeleton
                          variant="rounded"
                          width="10vw"
                          height={20}
                          sx={{ borderRadius: "5px" }}
                        />
                      </div>
                    </div>
                    <hr />
                  </>
                ))
              ) : clientCourses?.length > 0 ? (
                <Table className="mt-4">
                  <TableHead>
                    <TableRow>
                      <TableCell>Image</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {clientCourses?.map((item) => (
                      <CourseTabItem
                        key={item.id}
                        title={item.name}
                        status={item.status}
                        img={item.website_thumbnail_url}
                      />
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="NoDataAvailable">
                  {NoDataSvg}
                  <p>No Courses Taken</p>
                </div>
              )}
            </CustomTabPanel>
            {/* <CustomTabPanel value={selectedTab} index={1}>
            <div className="Tab--Panel Worksheets--Panel">
              <div className="WorksheetItem">
                <h6>Sleep Activity</h6>
                <span>Assigned on : 10 may 2023</span>
                <span>Weekly</span>
              </div>
            </div>
          </CustomTabPanel> */}

            {/* Notes Panel */}
            <CustomTabPanel value={selectedTab} index={4}>
              <div className="Tab--Panel Notes--Panel">
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: "bold",
                      mb: 2,
                      color: "var(--primary-base-color)",
                    }}
                  >
                    Add Note or Comment
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Type your note or comment here..."
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      onKeyPress={handleKeyPress}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "var(--primary-base-color)",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "var(--primary-base-color)",
                          },
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleSendComment}
                      disabled={!comment.trim() || isSubmittingComment}
                      startIcon={
                        isSubmittingComment ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <Send />
                        )
                      }
                      sx={{
                        bgcolor: "var(--primary-base-color)",
                        color: "white",
                        borderRadius: "10px",
                        textTransform: "capitalize",
                        px: 3,
                        py: 1.5,
                        minWidth: "auto",
                        "&:hover": {
                          bgcolor: "var(--primary-base-color-darken)",
                        },
                        "&:disabled": {
                          bgcolor: "#ccc",
                          color: "#666",
                        },
                      }}
                    >
                      {isSubmittingComment ? "Sending..." : "Send"}
                    </Button>
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Existing Comments Section */}
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: "bold",
                        color: "var(--primary-base-color)",
                      }}
                    >
                      Previous Notes & Comments
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={() => fetchComments(1)}
                      disabled={loadingComments}
                      size="small"
                      sx={{
                        borderRadius: "10px",
                        textTransform: "capitalize",
                        borderColor: "var(--primary-base-color)",
                        color: "var(--primary-base-color)",
                        "&:hover": {
                          borderColor: "var(--primary-base-color-darken)",
                          bgcolor: "rgba(15, 103, 110, 0.04)",
                        },
                      }}
                    >
                      {loadingComments ? "Loading..." : "Refresh"}
                    </Button>
                  </Box>

                  {loadingComments ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 4 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : existingComments.length > 0 ? (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {existingComments.map((commentItem) => (
                        <Card
                          key={commentItem.id}
                          variant="outlined"
                          sx={{
                            borderRadius: "10px",
                            border: "1px solid #e0e0e0",
                            bgcolor:
                              commentItem.type === "note"
                                ? "#F8F9FA"
                                : "#FFFFFF",
                          }}
                        >
                          <CardContent sx={{ py: 2, px: 3 }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                mb: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {commentItem.expertImage && (
                                  <Avatar
                                    src={commentItem.expertImage}
                                    sx={{ width: 24, height: 24 }}
                                  />
                                )}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: "bold",
                                    color: "var(--primary-base-color)",
                                  }}
                                >
                                  {commentItem.author}
                                </Typography>
                                <Chip
                                  label={
                                    commentItem.type === "note"
                                      ? "Note"
                                      : "Comment"
                                  }
                                  size="small"
                                  sx={{
                                    bgcolor:
                                      commentItem.type === "note"
                                        ? "#E3F2FD"
                                        : "#F3E5F5",
                                    color:
                                      commentItem.type === "note"
                                        ? "#1976D2"
                                        : "#7B1FA2",
                                    fontSize: "10px",
                                    height: "20px",
                                  }}
                                />
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {commentItem.timestamp}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{ lineHeight: 1.6 }}
                            >
                              {commentItem.text}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 4,
                        bgcolor: "#F8F9FA",
                        borderRadius: 2,
                        border: "1px dashed #D0D0D0",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No notes or comments yet. Add the first one above!
                      </Typography>
                    </Box>
                  )}

                  {/* Pagination Controls */}
                  {existingComments.length > 0 && totalCommentsPages > 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 2,
                        mt: 3,
                        pt: 2,
                        borderTop: "1px solid #e0e0e0",
                      }}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<NavigateBefore />}
                        onClick={handlePreviousPage}
                        disabled={commentsPage <= 1}
                        size="small"
                        sx={{
                          borderRadius: "10px",
                          textTransform: "capitalize",
                          borderColor: "var(--primary-base-color)",
                          color: "var(--primary-base-color)",
                          "&:hover": {
                            borderColor: "var(--primary-base-color-darken)",
                            bgcolor: "rgba(15, 103, 110, 0.04)",
                          },
                          "&:disabled": {
                            borderColor: "#ccc",
                            color: "#ccc",
                          },
                        }}
                      >
                        Previous
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        Page {commentsPage} of {totalCommentsPages}
                      </Typography>
                      <Button
                        variant="outlined"
                        endIcon={<NavigateNext />}
                        onClick={handleNextPage}
                        disabled={commentsPage >= totalCommentsPages}
                        size="small"
                        sx={{
                          borderRadius: "10px",
                          textTransform: "capitalize",
                          borderColor: "var(--primary-base-color)",
                          color: "var(--primary-base-color)",
                          "&:hover": {
                            borderColor: "var(--primary-base-color-darken)",
                            bgcolor: "rgba(15, 103, 110, 0.04)",
                          },
                          "&:disabled": {
                            borderColor: "#ccc",
                            color: "#ccc",
                          },
                        }}
                      >
                        Next
                      </Button>
                    </Box>
                  )}
                </Box>
              </div>
            </CustomTabPanel>

            {/* Prescriptions panel */}
            {/* <CustomTabPanel value={selectedTab} index={2}>
              <div className="Tab--Panel Prescriptions--Panel">
                {loading ? (
                  Array.from(new Array(10)).map((_, index) => (
                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex align-items-center gap-2 justify-content-between">
                        <Skeleton
                          variant="rounded"
                          width="30%"
                          height={18}
                          sx={{ borderRadius: "10px" }}
                        />

                        <Skeleton variant="circular" width={22} height={15} />
                      </div>

                      <Skeleton
                        variant="rounded"
                        width="20%"
                        height={18}
                        sx={{ borderRadius: "10px" }}
                      />
                      <Skeleton
                        variant="rounded"
                        width="20%"
                        height={18}
                        sx={{ borderRadius: "10px" }}
                      />
                    </div>
                  ))
                ) : clientPrescriptions.length > 0 ? (
                  <>
                    <Table stickyHeader>
                     
                      <TableHead>
                        <TableRow>
                          <TableCell>Title</TableCell>
                          <TableCell>Note</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>File</TableCell>
                        </TableRow>
                      </TableHead>

                    
                      <TableBody>
                        {clientPrescriptions?.map((item) => (
                          <PrescriptionTabItem
                            Id={item.Id}
                            Title={item.Title}
                            Note={item.Note}
                            CreatedDateTime={item.CreatedDateTime}
                            DocsFile={item.DocsFile}
                          />
                        ))}
                      </TableBody>
                    </Table>

                  </>

                ) : (
                  <p style={{ fontWeight: "500", textAlign: "center" }}>
                    There are no prescription notes attached with{" "}
                    {clientViewDetails?.name}.
                  </p>
                )}
              </div>
            </CustomTabPanel> */}

            {/* Session Notes Panel */}
            {/* <CustomTabPanel value={selectedTab} index={5}>
              <div className="Tab--Panel SessionNotes--Panel">
                {loading ? (
                  Array.from(new Array(10)).map((_, index) => (
                    <div
                      style={{
                        width: "100%",
                        height: "70px",
                        backgroundColor: "rgb(0,0,0,0.07)",
                        padding: "10px",
                        borderRadius: "10px",
                      }}
                      className="d-flex justify-content-between"
                    >
                      <div className="d-flex flex-column gap-2">
                        <Skeleton
                          variant="rounded"
                          height={18}
                          width="20vw"
                          sx={{ borderRadius: "10px" }}
                        />
                        <Skeleton
                          variant="rounded"
                          height={18}
                          width="15vw"
                          sx={{ borderRadius: "10px" }}
                        />
                      </div>

                      <div className="CTA--Container d-flex gap-2 align-items-center">
                        <Skeleton
                          variant="rounded"
                          height={38}
                          width={86}
                          sx={{ borderRadius: "20px" }}
                        />
                      </div>
                    </div>
                  ))
                ) : sortedClientSessionNotes.length > 0 ? (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Session Name</TableCell>
                        <TableCell>Last Update Date</TableCell>
                        <TableCell>Time</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {sortedClientSessionNotes.map((item) => (
                        <SessionNoteTabItem
                          key={item.id}
                          id={item.id}
                          name={item.name}
                          date={item.date}
                          time={item.time}
                          onView={fetchSessionNotesById}
                        />
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p style={{ fontWeight: "500", textAlign: "center" }}>
                    {clientViewDetails?.name} has no session notes attached.
                  </p>
                )}
              </div>
            </CustomTabPanel> */}
          </div>
        </Grid>

        {isLoading ? (
          <Grid item xs={4}>
            <div
              className="ClientDetailsGrid--Right"
              style={{
                background: "#fff",
                borderRadius: "10px",
                padding: "20px",
              }}
            >
              <Skeleton variant="circular" width={110} height={110} />
              <Skeleton variant="text" width="30%" height={20} />

              <div className="d-flex align-items-center gap-2 ClientActions--Container">
                <Skeleton
                  variant="rounded "
                  width={168}
                  height={38}
                  sx={{ borderRadius: "20px" }}
                />
                <Skeleton
                  variant="rounded "
                  width={168}
                  height={38}
                  sx={{ borderRadius: "20px" }}
                />
              </div>

              <div className="d-flex align-items-center gap-2 CTA--Container">
                <Skeleton
                  variant="rounded"
                  width={335}
                  height={50}
                  sx={{ borderRadius: "10px" }}
                />
              </div>
            </div>
          </Grid>
        ) : (
          <Grid item xs={4}>
            <div className="ClientDetailsGrid--Right border w-100 rounded-3 p-3 position-relative">
              <div style={{ position: "absolute", top: "10px", right: "10px", display: "flex", gap: "8px" }}>
                <Button
                  onClick={handleEditOpen}
                  sx={{
                    minWidth: "auto",
                    padding: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#f5f5f5",
                    color: "#414141",
                    "&:hover": {
                      backgroundColor: "#e0e0e0",
                    },
                  }}
                >
                  <EditIcon sx={{ fontSize: "20px" }} />
                </Button>
                {tempAdminEmails?.includes(currentUser?.Emailid) && (
                <Button
                  onClick={handleDeleteOpen}
                  sx={{
                    minWidth: "auto",
                    padding: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#f5f5f5",
                    color: "#d32f2f",
                    "&:hover": {
                      backgroundColor: "#ffebee",
                    },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: "20px" }} />
                </Button>
                )}
              </div>
              <img
                src={profile?.image}
                width={110}
                height={110}
                style={{ backgroundColor: "rgb(0,0,0,0.07)" }}
              />
              <h6>{profile.name}</h6>
              <p>{profile?.phone}</p>
              <p>{profile?.email}</p>
              <span>{profile.country}</span>
              <span>{profile?.dob ? normalizeDate(profile?.dob) : null}</span>

              {profile?.country?.toLowerCase() == "australia" && medirecordsStatus && (
                <div>
                  {(() => {
                    const message = medirecordsStatus?.data?.message || "";
                    const errors = medirecordsStatus?.data?.errors || [];
                    const isToast = medirecordsStatus?.data?.is_toast || false;
                    
                    let statusColor = "#6c757d"; // default gray
                    let hoverColor = "#5a6268";
                    let tooltipTitle = "";
                    let tooltipContent = null;
                    let StatusIcon = null;
                    
                    if (message === "Patient Synced Successfully.") {
                      statusColor = "#28a745"; // green
                      hoverColor = "#218838";
                      tooltipTitle = "✓ Synced Successfully";
                      tooltipContent = "Patient data has been successfully synced with Medirecords.";
                      StatusIcon = CheckCircle;
                    } else if (message === "Patient Not Synced.") {
                      statusColor = "#ffc107"; // yellow/warning
                      hoverColor = "#e0a800";
                      tooltipTitle = "⚠ Not Synced";
                      tooltipContent = "Patient data is not synced. Click to sync now.";
                      StatusIcon = Warning;
                    } else if (message === "Patient Not Found") {
                      statusColor = "#dc3545"; // red
                      hoverColor = "#c82333";
                      tooltipTitle = "✗ Patient Not Found";
                      tooltipContent = "Patient not found in Medirecords.";
                      StatusIcon = Error;
                    }
                    
                    if (errors.length > 0) {
                      tooltipContent = (
                        <Box sx={{ fontSize: '12px' }}>
                          <Box sx={{ fontWeight: 600, mb: 0.5 }}>{tooltipTitle}</Box>
                          <Box sx={{ color: 'gray', mb: 1 }}>{message}</Box>
                          <Box sx={{ color: '#fff', fontWeight: 500 }}>Errors:</Box>
                          {errors.map((error, index) => (
                            <Box key={index} sx={{ color: '#fff', fontSize: '11px', mt: 0.5 }}>
                              • {error}
                            </Box>
                          ))}
                        </Box>
                      );
                    } else if (tooltipContent) {
                      tooltipContent = (
                        <Box sx={{ fontSize: '12px' }}>
                          <Box sx={{ fontWeight: 600, mb: 0.5 }}>{tooltipTitle}</Box>
                          <Box sx={{ color: '#666' }}>{tooltipContent}</Box>
                        </Box>
                      );
                    }
                    
                    return (
                      <Tooltip
                        title={tooltipContent || "Click to sync with Medirecords"}
                        arrow
                        placement="top"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: '#2c3e50',
                              color: 'white',
                              fontSize: '12px',
                              maxWidth: 300,
                              px: 1.5,
                              py: 1,
                              borderRadius: '8px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              '& .MuiTooltip-arrow': {
                                color: '#2c3e50',
                              }
                            }
                          }
                        }}
                      >
                        <Button
                          className="w-auto"
                          disabled={mrDisabled}
                          onClick={handleCheckInMR}
                          startIcon={StatusIcon && <StatusIcon sx={{ fontSize: 18 }} />}
                          sx={{
                            color: statusColor,
                            borderColor: statusColor,
                            border: "1px solid",
                            borderRadius: "8px",
                            padding: "6px 16px",
                            fontWeight: 500,
                            textTransform: "none",
                            transition: "all 0.3s ease",
                            backgroundColor: mrDisabled ? "transparent" : "transparent",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            "&:hover": {
                              borderColor: hoverColor,
                              color: hoverColor,
                              backgroundColor: mrDisabled ? "transparent" : `${statusColor}10`,
                              transform: "translateY(-1px)",
                              boxShadow: `0 2px 4px ${statusColor}40`,
                            },
                            "&:active": {
                              transform: "translateY(0px)",
                            },
                            "&:disabled": {
                              opacity: 0.6,
                              cursor: "not-allowed",
                            }
                          }}
                        >
                          Medirecord Sync
                        </Button>
                      </Tooltip>
                    );
                  })()}
                </div>
              )}

              {/* <div className="d-flex align-items-center gap-2 ClientActions--Container">
                <Button onClick={handleChat}>
                  <ChatOutlinedIcon />
                  Chat
                </Button>

                <Button onClick={handleNotesOpen}>
                  <TextSnippetOutlinedIcon />
                  Add Notes
                </Button>

               
              </div> */}
              <div className="d-flex flex-column  w-100 gap-2 mt-4">
                <Button
                  fullWidth
                  variant="text"
                  sx={{
                    borderRadius: "20px", // Rounded but not too much
                    bgcolor: "#f5f5f5",
                    color: "#414141",
                    fontSize: "16px", // 14px as in the default theme
                    textTransform: "capitalize",
                    "&:hover": {
                      background: "#f5f5f5",
                    },
                  }}
                  disabled={!profile?.user_id}
                  onClick={() => {
                    window.open(
                      `https://copilot.asksam.com.au/clinical/patients?client=${profile?.name}&id=${profile?.user_id}`,
                      "_blank"
                    );
                  }}
                >
                  <ArrowOutwardRounded />
                  View In Copilot
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  sx={{
                    borderRadius: "20px", // Rounded but not too much
                    bgcolor: "#f5f5f5",
                    color: "#414141",
                    fontSize: "16px", // 14px as in the default theme
                    textTransform: "capitalize",
                    "&:hover": {
                      background: "#f5f5f5",
                    },
                  }}
                  className=" rounded-3 d-flex align-items-center justify-content-center gap-3"
                  onClick={() => {
                    navigate(
                      `/expert/chat?patientID=${clientID}&patientName=${clientName}&type=redirect`
                    );
                  }}
                >
                  <ChatBubbleOutlineOutlined
                    sx={{ color: "black", fontSize: "20px" }}
                  />{" "}
                  Chat
                </Button>
                {/* <Button
                  fullWidth
                  variant="text"
                  sx={{
                    borderRadius: '20px', // Rounded but not too much
                    bgcolor: 'var(--primary-base-color)',
                    color: '#FFFFFF',
                    fontSize: '16px', // 14px as in the default theme
                    textTransform: 'capitalize',
                    '&:hover': {
                      background: "var(--primary-base-color-darken)"
                    }
                  }} className=" rounded-3 d-flex align-items-center justify-content-center gap-3"

                  onClick={handleNotesOpen}>
                  <TextSnippetOutlined sx={{ color: "white", fontSize: "20px" }} />
                  Add Notes
                </Button> */}
              </div>

              <div className="d-none align-items-center gap-2 CTA--Container">
                {/* <Button
                sx={{ flex: "1", minWidth: "0" }}
                variant="contained"
                className="PrimaryCTA"
                disableElevation
              >
                Book Appointment
              </Button> */}

                <AddAppointmentModal clientDetails={clientViewDetails} />
              </div>

              <span
                className="d-none"
                style={{ color: "#6A6C6A", fontSize: "14px" }}
              >
                Last Active :{" "}
                <b style={{ color: "#000" }}>{clientDetails.last_active}</b>
              </span>
            </div>
          </Grid>
        )}
      </Grid>

      {/* Session Notes Dialog */}
      <Dialog
        className="Dialog SessionNotesView--Dialog"
        open={openSessionNotesModal}
        onClose={handleSessionNotesClose}
        sx={{
          maxWidth: "610px",
          width: "98%",
          minWidth: "580px",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <DialogTitle>{sessionNoteView?.name}</DialogTitle>
        <DialogContent className="Dialog--Content">
          <Box p={3}>
            {/* Client Details */}
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Client Details
            </Typography>
            <Grid
              container
              spacing={2}
              className="sessionNoteView--ClientDetails"
            >
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle1" fontWeight="500">
                  Client’s Contact No:
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {sessionNoteView?.client_mobile_no || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle1" fontWeight="500">
                  Client’s DOB:
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {sessionNoteView?.client_dob || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle1" fontWeight="500">
                  Client’s Address:
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {sessionNoteView?.client_address || "N/A"}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Note Details */}
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Note Details
            </Typography>
            {sessionNoteView?.responses?.map((item, i) => (
              <Accordion
                key={item.id}
                className="sessionNoteView--Accordion mt-3"
                elevation={2}
                sx={{
                  backgroundColor: "#f9f9f9",
                  borderRadius: "10px",
                  mb: 2,
                  "&:before": { display: "none" }, // Hide default accordion divider line
                  boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
                  transition: "box-shadow 0.3s ease",
                  "&:hover": { boxShadow: "0 5px 12px rgba(0,0,0,0.15)" },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ArrowDropDownIcon
                      sx={{
                        transform: "rotate(0deg)",
                        transition: "transform 0.3s ease",
                        "&.Mui-expanded": { transform: "rotate(180deg)" },
                      }}
                    />
                  }
                  aria-controls={`panel-${i}-content`}
                  id={`panel-${i}-header`}
                  sx={{
                    backgroundColor: "white", // Customize the background of the accordion header
                    color: "var(--primary-base-color)",
                    borderRadius: "10px 10px 0 0",
                    padding: "10px 20px",
                    "&:hover": {
                      backgroundColor: "var(--primary-accent-color)",
                    },
                  }}
                >
                  <Typography
                    sx={{ textTransform: "capitalize", fontWeight: "bold" }}
                  >
                    {item.question_text}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    backgroundColor: "#fff",
                    padding: "16px 20px",
                    borderRadius: "0 0 10px 10px",
                  }}
                >
                  <Typography variant="body2">
                    {item.response_text && item.response_text[0]}

                    {item.sub_questions && (
                      <Box mt={2}>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                          Sub-questions:
                        </Typography>
                        <ul style={{ paddingLeft: "20px", margin: 0 }}>
                          {item?.sub_questions?.map((question) => (
                            <li key={question.id}>
                              <Typography variant="body2">
                                <strong>{question.question_text}:</strong>{" "}
                                {question.response_text[0]}
                              </Typography>
                            </li>
                          ))}
                        </ul>
                      </Box>
                    )}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Edit Client Details Dialog */}
      <Dialog
        open={openEditModal}
        onClose={handleEditClose}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "10px",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            Edit Client Details
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Update client information
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2, pb: 3 }}>
          {isSaving && (
            <div
              style={{
                width: "100%",
                height: "100%",
                background: "rgba(255, 255, 255, 0.8)",
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: "10",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "10px",
              }}
            >
              <CircularProgress />
            </div>
          )}

          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* First Name */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="First Name"
                value={editFormData.firstName}
                onChange={(e) => handleFormChange("firstName", e.target.value)}
                fullWidth
                required
              />
            </Grid>

            {/* Middle Name */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Middle Name"
                value={editFormData.middleName}
                onChange={(e) => handleFormChange("middleName", e.target.value)}
                fullWidth
              />
            </Grid>

            {/* Last Name */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Last Name"
                value={editFormData.lastName}
                onChange={(e) => handleFormChange("lastName", e.target.value)}
                fullWidth
                required
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12}>
              <TextField
                label="Email"
                type="email"
                value={editFormData.email}
                onChange={(e) => handleFormChange("email", e.target.value)}
                fullWidth
                required
              />
            </Grid>

            {/* Gender */}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Gender</InputLabel>
                <Select
                  value={editFormData.gender?.toLowerCase()}
                  onChange={(e) => handleFormChange("gender", e.target.value)}
                  label="Gender"
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                  <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Phone */}
            <Grid item xs={6}>
              <TextField
                label="Phone Number"
                value={editFormData.phone}
                onChange={(e) => handleFormChange("phone", e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Date of Birth"
                  value={editFormData.dob ? (
                    editFormData.dob.includes('T') ? dayjs(editFormData.dob) : 
                    editFormData.dob.match(/^\d{4}-\d{2}-\d{2}$/) ? dayjs(editFormData.dob) :
                    dayjs(editFormData.dob, "DD-MM-YYYY")
                  ) : null}
                  onChange={(newValue) => handleFormChange("dob", newValue ? normalizeDate(dayjs(newValue).format("YYYY-MM-DD")) : null)}
                  format="DD-MM-YYYY"
                  sx={{ width: "100%" }}
                  disableFuture={true}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      required: true,
                      fullWidth: true
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleEditClose}
            variant="outlined"
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveChanges}
            variant="contained"
            disabled={isSaving || !editFormData.firstName || !editFormData.lastName || !editFormData.email || !editFormData.gender}
            sx={{
              backgroundColor: "var(--primary-base-color)",
              "&:hover": {
                backgroundColor: "var(--primary-base-color-darken)",
              },
            }}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteModal}
        onClose={handleDeleteClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxWidth: "400px",
            margin: "16px",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="bold" color="error">
            Delete Client
          </Typography>
          <Typography variant="body2" color="textSecondary">
            This action cannot be undone
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2, pb: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete <strong>{profile?.name}</strong>? This will permanently remove all client data.
          </Typography>
          
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            To confirm, please type <strong>DELETE</strong> in the box below:
          </Typography>

          <TextField
            fullWidth
            value={deleteConfirmationText}
            onChange={(e) => setDeleteConfirmationText(e.target.value)}
            placeholder="Type DELETE to confirm"
            variant="outlined"
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: deleteConfirmationText === "DELETE" ? "success.main" : "error.main",
                },
                "&.Mui-focused fieldset": {
                  borderColor: deleteConfirmationText === "DELETE" ? "success.main" : "error.main",
                },
              },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
          <Button
            onClick={handleDeleteClose}
            variant="outlined"
            disabled={isDeleting}
            sx={{ minWidth: "100px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleteConfirmationText !== "DELETE" || isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ minWidth: "100px" }}
          >
            {isDeleting ? "Deleting..." : "Delete Client"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ClientDetailsView;
